// Pure helpers for the Insight page: how findings are grouped and counted,
// which chart each finding carries, and the scale its value is read on.
// Nothing here invents a number; every figure is read from the API payload.
import type { Insight, Verdict, Visual } from "@dashboard-intelligent/api/src/modules/industry/types";
import { formatMetric } from "../../format";
import { gradedScale, isGraded, trendLabel } from "../shared/metric";
import { markOptions, type Mark } from "../shared/visual";

export const VERDICT_ORDER: Verdict[] = ["attention", "watch", "good"];

export function groupByVerdict(insights: Insight[]): Record<Verdict, Insight[]> {
  const groups: Record<Verdict, Insight[]> = { attention: [], watch: [], good: [] };
  for (const insight of insights) groups[insight.verdict]?.push(insight);
  return groups;
}

export function countByVerdict(insights: Insight[]): Record<Verdict, number> {
  const groups = groupByVerdict(insights);
  return { attention: groups.attention.length, watch: groups.watch.length, good: groups.good.length };
}

/** A metric's history needs this many points to be drawn as a trend. */
export const TREND_POINTS_MIN = 3;

/** The finding's metric month by month, for a finding with no evidence chart. */
export function trendVisual(insight: Insight): Visual | null {
  const { metric } = insight;
  if (metric.trend.length < TREND_POINTS_MIN) return null;
  return {
    id: `${insight.id}-trend`,
    kind: "trend",
    title: `Tren bulanan ${metric.label}`,
    summary: `${trendLabel(metric)}.`,
    stageId: insight.stageId,
    format: metric.format,
    categoryLabel: "Periode",
    series: [{ key: "value", label: metric.label }],
    data: metric.trend.map((point) => ({ label: point.label, value: point.value })),
  };
}

export interface FindingPlan {
  insight: Insight;
  /** The chart the finding carries: its evidence, else its metric's own trend, else none. */
  visual: Visual | null;
  source: "evidence" | "trend" | null;
  mark: Mark | null;
  /** A level target drawn on the trend, when the metric is graded. */
  target: { value: number; label: string } | null;
  /** The scale end the value is read on, when it has one. */
  scale: number | null;
}

/** The plan of one finding. Insight is not capped: every chart takes its first honest mark. */
export function findingPlan(insight: Insight): FindingPlan {
  const evidence = insight.evidence;
  const trend = evidence ? null : trendVisual(insight);
  const visual = evidence ?? trend;
  const graded = isGraded(insight.metric) ? insight.metric : null;
  return {
    insight,
    visual,
    source: evidence ? "evidence" : trend ? "trend" : null,
    mark: visual ? markOptions(visual)[0]! : null,
    target: graded && visual?.kind === "trend" && visual.format === graded.format ? { value: graded.target.target, label: "Target" } : null,
    scale: graded ? gradedScale(graded) : null,
  };
}

/** The thresholds of a graded finding in words: "Waspada 48,9%, kritis 43,4%". */
export function thresholdText(insight: Insight): string | null {
  const { metric } = insight;
  if (!isGraded(metric)) return null;
  return `Waspada ${formatMetric(metric.target.warning, metric.format)}, kritis ${formatMetric(metric.target.critical, metric.format)}`;
}
